import {
  useActivateRoleMutation,
  useDeactivateRoleMutation,
  useGetAllRolesQuery,
} from '@/app/features/control-panel/role-permission';
import CustomTable from '@/components/CustomTable';
import PageTitle from '@/components/PageTitle';
import StatusTag from '@/components/StatusTag';
import { DISCLOSER_KEYS, FILTER_KEYS } from '@/constants/keys.constants';
import { useAppDiscloser, useAppFilters } from '@/hooks';
import useAuth from '@/hooks/useAuth';
import {
  ChevronDownIcon,
  MoreVerticalIcon,
  PencilEdit02Icon,
  SearchIcon,
} from '@/icons';
import DashboardLayout from '@/layouts/DashboardLayout';
import { getErrorMessage } from '@/lib/error';
import { toast } from '@/lib/toast';
import {
  Button,
  Dropdown,
  Flex,
  Input,
  Select,
  Space,
  Switch,
  type TableProps,
} from 'antd';
import ManageRole from './_components/ManageRole';

const ROLE_STATUS_CONFIG = [
  { label: 'Active', value: 'active', className: 'status status-success' },
  { label: 'Inactive', value: 'inactive', className: 'status status-error' },
];

const RolePermission = () => {
  // Use filters hook for pagination and filtering
  const {
    page,
    limit,
    filters,
    keyword,
    handleSearch,
    handleFilter,
    handlePagination,
  } = useAppFilters(FILTER_KEYS.ROLES_MANAGEMENT);

  // Use discloser hook for ManageRole drawer
  const [roleConfig, handleOpenRoleDrawer, handleCloseRoleDrawer] =
    useAppDiscloser(DISCLOSER_KEYS.ROLES_MANAGEMENT);

  const [activateRole, { isLoading: isActivating }] = useActivateRoleMutation();
  const [deactivateRole, { isLoading: isDeactivating }] =
    useDeactivateRoleMutation();

  const handleStatusToggle = async (id: string, currentStatus: boolean) => {
    try {
      if (currentStatus) {
        await deactivateRole(id).unwrap();
        toast('success', 'Role deactivated successfully');
      } else {
        await activateRole(id).unwrap();
        toast('success', 'Role activated successfully');
      }
    } catch (error) {
      toast('error', getErrorMessage(error) || 'Failed to update role status');
    }
  };

  // Get organization ID from auth
  const { organizationId } = useAuth();

  // Fetch roles data
  const {
    data: rolesData,
    isLoading,
    isFetching,
  } = useGetAllRolesQuery({
    params: {
      organization_id: organizationId,
      ...(keyword ? { name: keyword } : {}),
      is_active:
        filters?.status === 'active'
          ? true
          : filters?.status === 'inactive'
            ? false
            : undefined,
      page,
      limit,
    },
  });

  // Transform API data for table
  const tableData =
    rolesData?.data?.data?.map((role: any) => ({
      ...role,
      id: role.id,
      roleName: role.name,
      assignedUsers: role.assigned_user_count || 0,
      permissionCount: role.permissions?.length || 0,
      descriptions: role.description || 'n/a',
      status: role.is_active ? 'active' : 'inactive',
    })) || [];

  const columns: TableProps<any>['columns'] = [
    {
      title: 'Role Name',
      dataIndex: 'roleName',
      // align: "center",
      render: (val: string) => val || 'n/a',
    },

    {
      title: 'Assigned Users',
      dataIndex: 'assignedUsers',
      render: (val) => val ?? 'n/a',
    },
    {
      title: 'Permissions',
      dataIndex: 'permissionCount',
      render: (val) => val ?? 'n/a',
    },
    {
      title: 'Descriptions',
      dataIndex: 'descriptions',
      render: (val) => val ?? 'n/a',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      align: 'center',
      render: (status: string) => (
        <StatusTag value={status} config={ROLE_STATUS_CONFIG} />
      ),
    },
    {
      title: 'Actions',
      align: 'center',
      width: 100,
      render: (record) => (
        <Dropdown
          trigger={['click']}
          placement="bottomRight"
          menu={{
            items: [
              {
                key: 'edit',
                label: 'Edit',
                icon: <PencilEdit02Icon size={18} />,
                onClick: () =>
                  handleOpenRoleDrawer({ type: 'UPDATE', data: record }),
              },
              {
                key: 'status',
                onClick: () => handleStatusToggle(record.id, record.is_active),
                label: (
                  <Flex justify="space-between" align="center" gap={12}>
                    <Switch
                      size="small"
                      checked={record.is_active}
                      loading={isActivating || isDeactivating}
                    />
                    <span>{record.is_active ? 'Active' : 'Inactive'}</span>
                  </Flex>
                ),
              },
            ],
          }}
        >
          <Button
            size="small"
            className="action-btn"
            icon={<MoreVerticalIcon size={20} />}
          />
        </Dropdown>
      ),
    },
  ];

  return (
    <DashboardLayout title="Roles & Permissions">
      <PageTitle
        classNames={{ root: 'items-start' }}
        title="Roles & Permissions"
        items={[
          { title: 'Dashboard', path: '/admin/control-panel/dashboard' },
          { title: 'Summary' },
        ]}
      >
        <div className="flex flex-col gap-4">
          {/* Row 1: Filters */}
          <Space className="filter-section" wrap>
            <Input
              size="large"
              className="shrink-0 w-72 custom-placeholder"
              placeholder="Search Role Name"
              prefix={<SearchIcon size={20} className="text-white" />}
              allowClear
              value={filters?.search}
              onChange={handleSearch}
            />

            <Select
              allowClear
              size="large"
              placement="bottomRight"
              placeholder="Status"
              style={{ flexShrink: 0, width: 130 }}
              suffixIcon={<ChevronDownIcon size={24} className="text-white" />}
              value={filters?.status}
              onChange={(value) => handleFilter('status', value)}
              options={[
                { value: 'active', label: 'Active' },
                { value: 'inactive', label: 'Inactive' },
              ]}
            />
          </Space>

          {/* Row 2: Button (last row, right aligned) */}
          <div className="flex justify-end">
            <Space>
              <Button
                size="large"
                className="btn-primary !font-bold !text-neutral-700 !text-base disabled:hover:!text-neutral-700 disabled:hover:opacity-80"
                onClick={() => handleOpenRoleDrawer({ type: 'ADD' })}
              >
                Create Role
              </Button>
            </Space>
          </div>
        </div>
      </PageTitle>

      <div className="mt-5">
        <CustomTable
          page={page}
          limit={limit}
          hideRowSelect
          columns={columns}
          dataSource={tableData}
          tableRootClassName="border-0 p-0"
          onPaginate={handlePagination}
          customTitle=""
          loading={isLoading || isFetching}
          total={rolesData?.data?.meta?.total || 0}
        />
      </div>

      {/* Manage Role Drawer */}
      <ManageRole
        data={roleConfig.data}
        open={roleConfig.open}
        handleClose={handleCloseRoleDrawer}
        id={roleConfig.data?.id}
      />
    </DashboardLayout>
  );
};

export default RolePermission;
